/**
 * TagPhotoPanel (device check, phase 3 — 11/2/2026; mockup 2-po-guard.png,
 * left column).
 *
 * Wherever the office picks or orders parts for a device (quote editor, PO
 * review), the device's TAG PHOTO sits next to the parts, with a "Tag photo
 * vs. record" table. Parts are ordered from the record, so the record must
 * be held up against the photo before anyone presses Send.
 *
 * - Photo: the latest nameplate photo saved to the device (click to zoom).
 *   No photo → the can't-read photos if any, else "No tag photo yet".
 * - Table: make / model / size / type / serial on the record. A row is red
 *   when the device's own label disagrees with the record (the AISD case:
 *   label "6\" Wilkins 350ASTDA", record model "350"); green ✓ when the
 *   device is Checked against the tag (the record IS what the tag said).
 * - The check chip, and an amber "never checked by a tech" note saying where
 *   the record came from.
 *
 * Reads GET /api/assets/:id/device-check (phase 1). Changes nothing.
 */
import { useState } from "react";
import { cn, formatDate } from "@/lib/utils";
import { Dialog } from "@/components/ui/dialog";
import { DeviceCheckChip, useDeviceCheck, type DeviceCheckData } from "@/components/device-check-card";

export interface TagPanelRecord {
    name?: string | null;
    manufacturer?: string | null;
    model?: string | null;
    size_label?: string | null;
    assembly_type?: string | null;
    serial_number?: string | null;
    external_source_key?: string | null;
}

type FactKey = "manufacturer" | "model" | "size_label" | "assembly_type" | "serial_number";
const ROWS: Array<{ key: FactKey; label: string }> = [
    { key: "manufacturer", label: "Make" },
    { key: "model", label: "Model" },
    { key: "size_label", label: "Size" },
    { key: "assembly_type", label: "Type" },
    { key: "serial_number", label: "Serial" },
];

const norm = (v: string) => v.toLowerCase().replace(/["'″”\s-]+/g, "");

/**
 * What the device's own label says about make / model / size, when it says
 * something the record doesn't. Pure, so it is tested on its own.
 * "6\" Wilkins 350ASTDA" vs model "350" → model mismatch "350ASTDA".
 */
export function labelDisagreements(rec: TagPanelRecord): Partial<Record<FactKey, string>> {
    const label = (rec.name ?? "").trim();
    if (!label) return {};
    const tokens = label.split(/[\s/,]+/).filter(Boolean);
    const out: Partial<Record<FactKey, string>> = {};
    const model = (rec.model ?? "").trim();
    const make = (rec.manufacturer ?? "").trim();
    // A model-looking token: has a digit, isn't a size.
    const modelTokens = tokens.filter((t) => /\d/.test(t) && !/["″”]$/.test(t) && !/^\d+(-\d+\/\d+|\/\d+)$/.test(t));
    if (model && modelTokens.length > 0 && !modelTokens.some((t) => norm(t) === norm(model))) {
        out.model = modelTokens[modelTokens.length - 1];
    }
    if (make && tokens.length > 1) {
        const knownMakes = ["watts", "wilkins", "zurn", "febco", "ames", "conbraco", "apollo", "cla-val", "flomatic"];
        const labelMake = tokens.find((t) => knownMakes.includes(t.toLowerCase()));
        if (labelMake && norm(labelMake) !== norm(make) && !(norm(labelMake) === "zurn" && norm(make) === "wilkins")) {
            out.manufacturer = labelMake;
        }
    }
    const sizeTok = tokens.find((t) => /["″”]$/.test(t));
    const size = (rec.size_label ?? "").trim();
    if (sizeTok && size && norm(sizeTok) !== norm(size)) out.size_label = sizeTok;
    return out;
}

export function TagPhotoPanel({
    assetId,
    record,
    compact = false,
    data: injected,
}: {
    assetId: string;
    record: TagPanelRecord;
    compact?: boolean;
    /** Tests / screenshots pass data directly instead of fetching. */
    data?: DeviceCheckData;
}) {
    const q = useDeviceCheck(injected ? null : assetId);
    const d = injected ?? q.data;
    const [zoom, setZoom] = useState(false);
    // Defensive: an older server (or a stub) may answer without photos.
    const photos = Array.isArray(d?.photos) ? d.photos : [];
    const tag = photos.find((p) => p.kind === "nameplate") ?? null;
    const other = photos.filter((p) => p.kind !== "nameplate");
    const label = labelDisagreements(record);
    const checked = d?.state === "checked";
    const cameFrom = record.external_source_key?.startsWith("st:") ? "ServiceTitan" : "the office";

    return (
        <div className="space-y-2 rounded-xl border bg-card p-3" data-testid="tag-photo-panel">
            {tag ? (
                <button type="button" onClick={() => setZoom(true)} className="block w-full" title="Click to zoom">
                    <img
                        src={tag.url}
                        alt="Tag photo"
                        className={cn("w-full rounded-lg border bg-muted object-cover", compact ? "h-28" : "h-44")}
                    />
                </button>
            ) : other.length > 0 ? (
                <div className="grid grid-cols-3 gap-1">
                    {other.slice(0, 3).map((p) => (
                        <img key={p.id} src={p.url} alt={p.kind} className="h-20 w-full rounded border object-cover" />
                    ))}
                </div>
            ) : (
                <div className="flex h-20 items-center justify-center rounded-lg border border-dashed text-xs text-foreground/60">
                    No tag photo yet — the next tech there takes one.
                </div>
            )}
            {tag && (
                <div className="text-[11px] text-foreground/60">
                    Tag photo · {formatDate(tag.at)}
                    {tag.takenByName ? ` · ${tag.takenByName}` : ""} · click to zoom
                </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-1">
                <div className="text-xs font-bold">Tag photo vs. record</div>
                <DeviceCheckChip data={d} />
            </div>
            <table className="w-full text-xs" data-testid="tag-vs-record">
                <thead>
                    <tr className="text-[10px] uppercase tracking-wide text-foreground/60">
                        <th className="py-0.5 text-left font-semibold"> </th>
                        <th className="py-0.5 text-left font-semibold">Device record</th>
                        <th className="py-0.5 text-left font-semibold">Tag</th>
                    </tr>
                </thead>
                <tbody>
                    {ROWS.map((r) => {
                        const v = (record[r.key] ?? "").toString();
                        const disagree = label[r.key];
                        return (
                            <tr key={r.key} data-testid={`tag-row-${r.key}`} data-bad={disagree ? "1" : undefined}>
                                <td className="py-0.5 pr-2 text-foreground/70">{r.label}</td>
                                <td
                                    className={cn(
                                        "py-0.5 pr-2 font-semibold",
                                        disagree && "text-red-700 dark:text-red-300",
                                    )}
                                >
                                    {v ? (r.key === "assembly_type" ? v.toUpperCase() : v) : "—"}
                                    {disagree ? " ✗" : ""}
                                </td>
                                <td className="py-0.5">
                                    {disagree ? (
                                        <span className="font-semibold text-red-700 dark:text-red-300">
                                            label says {disagree}
                                        </span>
                                    ) : checked ? (
                                        <span className="text-emerald-700 dark:text-emerald-300">✓ matches</span>
                                    ) : (
                                        <span className="text-foreground/50">not checked</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {d?.state && d.state !== "checked" && (
                <div
                    className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-600/40 dark:bg-amber-900/20 dark:text-amber-100"
                    data-testid="never-checked-note"
                >
                    <b>Device record: never checked by a tech.</b> It came from {cameFrom}.{" "}
                    {tag
                        ? "Hold the record up to the tag photo above before ordering."
                        : "No tag photo yet, so nobody has proven it."}
                </div>
            )}
            <Dialog open={zoom} onClose={() => setZoom(false)} title="Tag photo">
                {tag && zoom && <img src={tag.url} alt="Tag photo, zoomed" className="w-full rounded-lg" />}
            </Dialog>
        </div>
    );
}
